import "firebase/auth"

import * as firebase from "firebase/app"

import client from "../apollo/client"
import { navigate } from "gatsby"

// if (!!firebase && typeof firebase.initializeApp === "function")
//   firebase.initializeApp({
//     apiKey: process.env.GATSBY_FIREBASE_API_KEY,
//     authDomain: process.env.GATSBY_AUTH_DOMAIN,
//     databaseURL: process.env.GATSBY_FIREBASE_DATABASE_URL,
//     projectId: process.env.GATSBY_FIREBASE_PROJECT_ID,
//     storageBucket: process.env.GATSBY_FIREBASE_STORAGE_BUCKET,
//     messagingSenderId: process.env.GATSBY_FIREBASE_MESSAGING_SENDER_ID,
//     appId: process.env.GATSBY_FIREBASE_APP_ID,
//     measurementId: process.env.GATSBY_FIREBASE_MEASUREMENT_ID,
//   })

const clearUser = () => {
  window.authenticated_user = null
  window.localStorage.removeItem("displayUpgrade")
  client.clearStore()
}

/**
 * Retrieves the authenticated Firebase user auth object
 * from memory when called. If no user object is cached,
 * it calls setAuthenticatedUser to listen for an auth state
 * change and returns the user from that.
 * @usage const user = getAuthenticatedUser()
 * @returns {Object} user the firebase user auth object
 */
export const getAuthenticatedUser = () => {
  const user = firebase.auth().currentUser
  if (!user) setAuthenticatedUser()
  return user ? user : window.authenticated_user
}

/**
 *
 */
export const setAuthenticatedUser = async () => {
  const unsub = firebase.auth().onAuthStateChanged(function (user) {
    if (user) {
      window.authenticated_user = firebase.auth().currentUser
    } else {
      window.authenticated_user = null
    }
    unsub()
  })
}

/**
 *  Resets the current user to pristine status.
 */
export const resetUser = async () => {
  return new Promise((resolve, reject) => {
    // Clear Apollo Client cache store
    client.clearStore()
    // Resolve user ID token
    const unsub = firebase.auth().onAuthStateChanged(function (user) {
      if (user) {
        firebase
          .auth()
          .currentUser.getIdToken(true)
          .then(token => {
            resolve({ user, token })
          })
          .catch(error => {
            console.error(error)
            reject("No firebase user token.")
          })
      } else {
        reject("No firebase user.")
      }
      unsub()
    })
  })
}

/**
 * Used by the Apollo Client configuration `apollo-link-context` to
 * generate a new, valid auth token for each GraphQL endpoint
 * operation.
 * @usage const token = await generateAuthenticationToken()
 * @return {Promise} token user auth token
 */
export const generateAuthenticationToken = async () => {
  return new Promise(async (resolve, reject) => {
    const unsub = firebase.auth().onAuthStateChanged(function (user) {
      if (user) {
        firebase
          .auth()
          .currentUser.getIdToken(true)
          .then(token => {
            resolve(token)
          })
          .catch(error => {
            console.error(error)
            resolve("client")
          })
      } else {
        console.warn(
          "LOG: [Authentication] generateAuthenticationToken found no firebase user."
        )
        resolve("client")
      }
      unsub()
    })
  })
}

export const handleLoginAuthListener = () => {
  const unsub = firebase.auth().onAuthStateChanged(function (user) {
    if (user) {
      handleAuthenticationSuccess(user)
      unsub()
    }
  })
}

export const handleAuthenticationSuccess = async user => {
  if (!user) return
  console.log("LOG: [Authentication] Handle authentication success", user)
  window.localStorage.setItem("isLoggedIn", true)
  setAuthenticatedUser()
  window.localStorage.removeItem("isAuthRedirect")
  navigate("/a/account#login")
}

export const authenticateGoogleProvider = async () => {
  window.localStorage.setItem("isAuthRedirect", true)
  let provider = new firebase.auth.GoogleAuthProvider()
  firebase.auth().signInWithRedirect(provider)
}

export const authenticateAppleProvider = async () => {
  let provider = new firebase.auth.OAuthProvider("apple.com")
  provider.addScope("email")
  provider.addScope("name")
  firebase.auth().signInWithRedirect(provider)
}

export const authenticateUsernamePassword = async (email, password) => {
  let _error = null
  return new Promise((resolve, reject) => {
    firebase
      .auth()
      .signInWithEmailAndPassword(email, password)
      .then(user => {
        // handleSuccessfulAuth(user)
        handleAuthenticationSuccess(user)
        resolve(user)
      })
      .catch(error => {
        switch (error.code) {
          case "auth/email-already-in-use":
            _error = {
              field: "email",
              message: `This email belongs to a current user.  If that is you, please trying logging in instead.`,
            }
            break
          case "auth/invalid-email":
            _error = {
              field: "email",
              message: `The email you entered is invalid.  Please check the email is in the proper format and try again.`,
            }
            break
          case "auth/user-not-found":
            _error = {
              field: "email",
              message: `A user with this email does not exist.  Please check the email and try again.`,
            }
            break
          case "auth/wrong-password":
            _error = {
              field: "password",
              message: `The password you entered is incorrect.`,
            }
            break
          case "auth/too-many-requests":
            _error = {
              field: "password",
              message: `Too many unsuccessful login requests in a row.  Please wait a few minutes and try again.`,
            }
            break
          default:
            _error = {
              ui: "toast",
              field: "email",
              message: `An error occurred.  Please check the form and try again.`,
            }
        }
        reject(_error)
      })
  })
}

export const authenticateCustomToken = async token => {
  firebase
    .auth()
    .signInWithCustomToken(token)
    .then(user => {
      console.log("LOG: [Authentication] -> authenticateCustomToken success.", {
        user,
        token,
      })
      handleAuthenticationSuccess(user)
    })
    .catch(function (error) {
      console.log("LOG: [Authentication] -> authenticateCustomToken error.", JSON.stringify({
        error,
        message: error.message,
        code: error.code,
        token,
      }))
      window.location.assign("/a/login")
    })
}

export const registerEmailPasswordAccount = async (
  email,
  password,
  firstName
) => {
  let _error = null
  return new Promise((resolve, reject) => {
    firebase
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then(user => {
        firebase
          .auth()
          .currentUser.updateProfile({
            displayName: firstName,
          })
          .then(function () {
            handleAuthenticationSuccess(user)
            resolve(user)
          })
          .catch(function (error) {
            console.error(error)
            _error = {
              field: "name",
              message: `Your account was created but an error occurred while updating your name.  Please refresh the page and login, and then under the setting page, update your name.`,
            }
            reject(_error)
          })
      })
      .catch(error => {
        switch (error.code) {
          case "auth/email-already-in-use":
            _error = {
              field: "email",
              message: `This email belongs to a current user.  If that is you, please trying logging in instead.`,
            }
            break
          case "auth/invalid-email":
            _error = {
              field: "email",
              message: `The email you entered is invalid.  Please check the email is in the proper format and try again.`,
            }
            break
          case "auth/user-not-found":
            _error = {
              field: "email",
              message: `A user with this email does not exist.  Please check the email and try again.`,
            }
            break
          case "auth/weak-password":
            _error = {
              field: "password",
              message: `The password you entered is too weak.  Please use a password with at least 8 characters.`,
            }
            break
          case "auth/too-many-requests":
            _error = {
              field: "password",
              message: `Too many unsuccessful login requests in a row.  Please wait a few minutes and try again.`,
            }
            break
          default:
            _error = {
              ui: "toast",
              field: "email",
              message: `An error occurred.  Please check the form and try again.`,
            }
        }
        reject(_error)
      })
  })
}

export const resetUserPassword = async email => {
  let _error = null
  return new Promise((resolve, reject) => {
    firebase
      .auth()
      .sendPasswordResetEmail(email)
      .then(() => {
        resolve(true)
      })
      .catch(error => {
        switch (error.code) {
          case "auth/invalid-email":
            _error = {
              field: "email",
              message: `The email you entered is invalid.  Please check the email is in the proper format and try again.`,
            }
            break
          case "auth/user-not-found":
            _error = {
              field: "email",
              message: `A user with this email does not exist.  Please check the email and try again.`,
            }
            break
          default:
            _error = {
              ui: "toast",
              field: "email",
              message: `An error occurred.  Please check the form and try again.`,
            }
        }
        reject(_error)
      })
  })
}

export const redirectOnSignout = customRedirect => {
  if (customRedirect) window.location.assign(customRedirect)
  else window.location.assign("/a/login#so")
}

export const signOut = async customRedirect => {
  try {
    await firebase.auth().signOut()
    clearUser()
    window.localStorage.removeItem("isLoggedIn")
    redirectOnSignout(customRedirect)
  } catch (error) {
    console.error("Firebase `signOut` error", error)
    alert(
      "There was an issue while logging out.  Usually this is temporary.  You will now be redirected back to your journal.  Please wait a few minutes and try logging out again.  If the issue persists, contact support."
    )
    window.location.assign("/a/journal")
  }
}
